import { useState, useEffect } from 'react';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import InquiryReview from './components/InquiryReview';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getCurrentFYear } from '../../utils/financialYear';

const steps = ['Client Details', 'Product Details', 'Review & Submit'];

// Utility function for deep merging objects
const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key]);
        } else {
            output[key] = source[key];
        }
    }
    return output;
};

const OrderMaster = ({ mode, connectionString, onPopupCancel }) => {
    const { user, selectedCompany } = useAuth();
    const fYear = getCurrentFYear();
    // alert(mode)
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const location = useLocation();
    const { SOAutoID } = useParams(); // Extract SOAutoID from URL
    const searchParams = new URLSearchParams(location.search);
    const quotationId = searchParams.get("quotationId");

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [consignerEnabled, setConsignerEnabled] = useState(true);
    const [transporterEnabled, setTransporterEnabled] = useState(true);
    const [exportEnabled, setExportEnabled] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [selectedSOIDs, setSelectedSOIDs] = useState(SOAutoID ? [SOAutoID] : []);
    const [isOrderLocked, setIsOrderLocked] = useState(false);

    // Fetch quotation data for 'order' mode
    useEffect(() => {
        if (mode === "order" && quotationId) {
            // console.log("Mode is 'order' and Received Quotation ID:", quotationId);
            const fetchQuotationData = async () => {
                try {
                    const res = await axios.get(`${API_URL}/quot-Order-ref/${quotationId}`);
                    const data = res.data;
                    console.log('Fetched quotation data:', data);
                    setFormData(data);
                    setClientId(data.client?.clientId || null); // Extract clientId from quotation data
                } catch (error) {
                    console.error('Error fetching quotation data:', error);
                    setSnackbar({ open: true, message: 'Failed to fetch quotation data.', severity: 'error' });
                }
            };
            fetchQuotationData();
        }
    }, [mode, quotationId]);

    // Fetch sales order data for 'edit' mode
    useEffect(() => {
        const fetchSalesOrders = async () => {
            try {
                // console.log('Fetching sales order data for SOIDs:', selectedSOIDs);
                const response = await axios.post(`${API_URL}/orders-getInfo`, { selectedSOIDs });
                const data = response.data;
                console.log('Fetched sales order data:', data);

                // Handle both single object and array responses
                const salesOrderData = Array.isArray(data) ? data[0] : data;
                if (!salesOrderData) {
                    throw new Error('No sales order data returned');
                }

                // Transform data to match formData structure
                setFormData(salesOrderData);
                setClientId(salesOrderData.client?.clientId || null);
            } catch (error) {
                console.error('Error fetching sales orders:', error.response?.data || error.message);
                setSnackbar({ open: true, message: 'Failed to fetch sales order data.', severity: 'error' });
            }
        };

        if (mode === 'edit' && selectedSOIDs.length > 0) {
            fetchSalesOrders();
        }
    }, [mode, selectedSOIDs]);

    useEffect(() => {
        if (mode !== 'edit' || !SOAutoID) return;

        const checkLock = async () => {
            try {
                const res = await axios.get(
                    `${API_URL}/check-challan-created-from-order/${SOAutoID}`
                );
                if (res.data?.orderLocked) {
                    setIsOrderLocked(true);
                }
            } catch (e) {
                console.error('Order lock check failed', e);
            }
        };

        checkLock();
    }, [mode, SOAutoID]);


    const handleNext = (stepData = {}) => {
        // console.log('Step data:', stepData);
        setFormData(prev => deepMerge(prev, stepData));
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleSubmit = async () => {
        try {
            const payload = {
                client: formData.client,
                product: formData.product,
                SOAutoID: SOAutoID,
                qoutId: parseInt(quotationId) || QoutID,
                companyId: selectedCompany?.CompId || null,
                AppRegID: selectedCompany?.AppRegID || null,
                addUserId: user?.UserId || null,
                fYear,
            };
            console.log('Submit payload:', payload);
            if (mode === 'add' || mode === 'order') {
                await axios.post(`${API_URL}/create-order/v2`, payload);
            } else if (mode === 'edit') {
                // console.log("PUT URL:", `${API_URL}/orders-update/${SOAutoID}`);
                // console.log("PUT Payload:", JSON.stringify(payload, null, 2));
                await axios.put(`${API_URL}/orders-update/${SOAutoID}`, payload);
            }

            setSnackbar({ open: true, message: mode === 'add' || mode === 'order' ? 'Order created successfully!' : 'Order updated successfully!', severity: 'success', });
            setTimeout(() => {
                if (typeof onPopupCancel === "function") onPopupCancel();
                else navigate(-1);
            }, 600);
            setActiveStep(0);
            setFormData({});

        } catch (error) {
            console.error('Error submitting order:', error);
            setSnackbar({ open: true, message: 'Failed to submit order.', severity: 'error' });
        }
    };

    const handleCancel = () => {
        if (typeof onPopupCancel === "function") {
            onPopupCancel();
        } else {
            navigate(-1);
        }
    };

    // Sync activeStep with URL changes (e.g., browser back button).
    // Note: by the time popstate fires, the browser has ALREADY moved history
    // back one entry — calling handleCancel()/navigate(-1) here would pop a
    // SECOND entry on top of that, overshooting past the intended page. Just
    // reset local wizard state; the browser's own navigation handles the page.
    useEffect(() => {
        const handlePopState = () => {
            setActiveStep(0);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ──────────────── Alt+C (Cancel) shortcut — works from any step ───────────
    // Attached here (the wizard's parent), not per-step, so it works regardless
    // of which step (Client Details / Product Details / Review) is active —
    // those step components don't each carry their own cancel concept.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyC") return;
            e.preventDefault();
            handleCancel();
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [handleCancel]);

    const disablePartyName =
        Array.isArray(formData?.product?.tableData) &&
        formData.product.tableData.some(
            row => row?.QoutID != null || row?.QoutDtsId != null
        );

    const QoutID = formData?.product?.tableData?.find(row => row?.QoutID != null)?.QoutID || null;

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {renderStepHeader('1️⃣', 'Client Details')}
                        <ClientDetails mode={mode} initialData={formData.client || {}} connectionString={connectionString} disablePartyName={disablePartyName} isOrderLocked={isOrderLocked}
                            onNext={(data) => {
                                setClientId(data.clientId || null); // Update clientId on client step completion
                                handleNext({ client: data });
                            }}
                        />
                    </>
                );

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails mode={mode} initialData={formData.product || {}} onNext={(data) => handleNext({ product: data })} clientData={formData.client || {}} connectionString={connectionString} isOrderLocked={isOrderLocked} checkQuotRef={disablePartyName}
                            onRowsChange={(rows) => {
                                setFormData(prev => ({
                                    ...prev,
                                    product: {
                                        ...(prev.product || {}),
                                        tableData: rows,
                                    }
                                }));
                            }} />
                    </>
                );

            case 2:
                return (
                    <InquiryReview formData={formData} selectedCompany={selectedCompany} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} connectionString={connectionString} mode={mode} isOrderLocked={isOrderLocked} />
                );

            default:
                return null;
        }
    };

    return (
        <Box maxWidth="lg" mx="auto" px={2} py={3} pb={0} >
            {/* <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600, mt: -1.8, mb: 1 }}>
                {mode === 'edit' ? 'Edit Order' : 'Add New Order'}
            </Typography> */}

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                {/* Back arrow — mobile/tablet only */}
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === 'edit' ? 'Edit Order' : 'Add New Order'}
                </Typography>
            </Box>

            <Paper elevation={4} sx={{ borderRadius: 3, p: 1.5 }}>
                {isOrderLocked && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Order is locked. Client Details and Product Details cannot be modified.
                    </Alert>
                )}
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Divider sx={{ mb: 0.5 }} />
                {getStepComponent(activeStep)}
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OrderMaster;